// Review controls shared by the documents list and the review panel (FR-01, FR-14):
// the relevance score and badge, the reviewer-decision pill, the stage pill
// (sc-11034), the out-of-date marker on finalized documents, and the "from thread"
// marker on replies tagged through their email thread (sc-11471).
import { CornerDownRight, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select';
import type { CaseDocument, DocumentStage, DocumentStatus, ReviewDecision } from '../../types';
import { DOCUMENT_STAGE_CONFIG, REVIEW_DECISION_CONFIG, REVIEW_DECISIONS, suggestedReviewDecision } from '../../types';
import { MANUAL_STAGES, statusStage } from './documentStage';
import { cn } from '../ui/utils';
import { IconTooltip } from '../ui/tooltip';
import { CLEAR_DECISION, relevanceOf } from './relevance';
import { UNDO_THREAD, isThreadTagged, threadMarkerLabel } from './threadTagging';

/** A row's relevance score, colored like the relevance groups, its reason on hover (FB-1024 #12). */
export function RelevanceBadge({ doc, score }: { doc: CaseDocument; score: number }) {
  const reason = (doc.metadata as Record<string, unknown> | undefined)?.relevance_reason;
  return (
    <IconTooltip label={typeof reason === 'string' && reason ? reason : `${score}% relevant to the request`}>
      <span
        data-testid="relevance-badge"
        aria-label={`${score}% relevant`}
        className={cn(
          'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none tabular-nums',
          score >= 80 ? 'bg-green-100 text-green-700' : score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
        )}
      >
        {score}%
      </span>
    </IconTooltip>
  );
}

/**
 * A row's reviewer decision (FR-01) as a small pill-shaped select. Undecided rows
 * show what the relevance score suggests, but nothing is stored until the
 * reviewer picks a value.
 */
export function ReviewDecisionSelect({
  doc,
  onChange,
  threadTaggedCount = 0,
  onUndoThread,
}: {
  doc: CaseDocument;
  onChange: (documentId: string, decision: ReviewDecision | null) => void;
  /** Replies this email tagged Responsive through its thread (sc-11471). */
  threadTaggedCount?: number;
  /** "Undo thread tagging": clear those replies. The menu offers it when both are set. */
  onUndoThread?: (documentId: string) => void;
}) {
  const decision = doc.review_decision ?? null;
  const suggestion = decision ? null : suggestedReviewDecision(relevanceOf(doc));
  const color = decision ? REVIEW_DECISION_CONFIG[decision].color : undefined;
  return (
    // The row is itself a button: keep clicks and keys — including ones from the
    // portalled menu, which bubble through the React tree — from selecting it.
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Select
        value={decision ?? ''}
        onValueChange={(value: string) => {
          if (value === UNDO_THREAD) onUndoThread?.(doc.id);
          else onChange(doc.id, value === CLEAR_DECISION ? null : (value as ReviewDecision));
        }}
      >
        <SelectTrigger
          size="sm"
          aria-label={`Review decision for ${doc.name}`}
          data-testid="review-decision"
          data-decision={decision ?? 'none'}
          className={cn(
            'h-5 data-[size=sm]:h-5 w-auto gap-0.5 rounded-full pl-2 pr-1.5 py-0 text-[11px] font-medium leading-none [&_svg]:!size-3',
            !decision && 'border-dashed bg-white text-gray-500'
          )}
          style={decision ? { color, borderColor: `${color}55`, backgroundColor: `${color}14` } : undefined}
        >
          <SelectValue placeholder={suggestion ? `Suggest: ${REVIEW_DECISION_CONFIG[suggestion].label}` : 'Decision'} />
        </SelectTrigger>
        <SelectContent>
          {REVIEW_DECISIONS.map((d) => (
            <SelectItem key={d} value={d}>
              {REVIEW_DECISION_CONFIG[d].label}
              {d === suggestion && <span className="ml-1.5 text-[10px] text-gray-400">(suggested)</span>}
            </SelectItem>
          ))}
          {decision && <SelectItem value={CLEAR_DECISION}>Clear decision</SelectItem>}
          {onUndoThread && threadTaggedCount > 0 && (
            <>
              <SelectSeparator />
              <SelectItem value={UNDO_THREAD} data-testid="undo-thread-tagging">
                Undo thread tagging ({threadTaggedCount} {threadTaggedCount === 1 ? 'reply' : 'replies'})
              </SelectItem>
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * A reply tagged Responsive through its email thread (sc-11471): "from thread", the
 * source email on hover, and an undo that clears this reply's decision only.
 */
export function ThreadDecisionBadge({
  doc,
  sourceName,
  onUndo,
}: {
  doc: CaseDocument;
  sourceName?: string;
  onUndo?: (documentId: string) => void;
}) {
  if (!isThreadTagged(doc) || !doc.review_decision) return null;
  return (
    <span
      data-testid="thread-decision"
      data-source-id={doc.review_decision_source_id ?? undefined}
      className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 pl-1.5 pr-1 py-0.5 text-[10px] font-medium leading-none text-emerald-800"
    >
      <IconTooltip label={threadMarkerLabel(sourceName)}>
        <span className="inline-flex items-center gap-0.5">
          <CornerDownRight className="h-3 w-3 flex-shrink-0" aria-hidden />
          from thread
        </span>
      </IconTooltip>
      {onUndo && (
        <IconTooltip label="Undo: clear this reply's decision">
          <button
            type="button"
            aria-label={`Undo thread tagging for ${doc.name}`}
            data-testid="thread-decision-undo"
            className="rounded-full p-0.5 hover:bg-emerald-100"
            onClick={(e) => {
              e.stopPropagation();
              onUndo(doc.id);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <X className="h-2.5 w-2.5" aria-hidden />
          </button>
        </IconTooltip>
      )}
    </span>
  );
}

/** A stage's colored dot, as on the stage badge and in the stage menus. */
function StageDot({ color }: { color: string }) {
  return <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden />;
}

/** A row's stage when it can't be changed by hand (container, analyzing, failed, completed). */
export function DocumentStageBadge({ stage }: { stage: DocumentStage }) {
  const { label, color } = DOCUMENT_STAGE_CONFIG[stage];
  return (
    <span
      data-testid="document-stage"
      data-stage={stage}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium leading-none"
      style={{ color, backgroundColor: `${color}1a` }}
    >
      <StageDot color={color} />
      {label}
    </span>
  );
}

/**
 * A row's stage as a small pill-shaped select (sc-11034), built like the decision
 * pill beside it. It offers the stages a reviewer sets by hand; `analyzed` says
 * whether `unprocessed` reads "Suggestions ready" or "Not analyzed" for this row.
 */
export function DocumentStageSelect({
  doc,
  analyzed,
  onChange,
}: {
  doc: CaseDocument;
  analyzed: boolean;
  onChange: (documentId: string, status: DocumentStatus) => void;
}) {
  const stage = statusStage(doc.status, analyzed);
  const { label, color } = DOCUMENT_STAGE_CONFIG[stage];
  return (
    // The row is itself a button: keep clicks and keys — including ones from the
    // portalled menu, which bubble through the React tree — from selecting it.
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Select
        value={doc.status}
        onValueChange={(value: string) => {
          if (value !== doc.status) onChange(doc.id, value as DocumentStatus);
        }}
      >
        <SelectTrigger
          size="sm"
          aria-label={`Stage for ${doc.name}`}
          data-testid="document-stage"
          data-stage={stage}
          className="h-5 data-[size=sm]:h-5 w-auto gap-0.5 rounded-full pl-2 pr-1.5 py-0 text-[11px] font-medium leading-none [&_svg]:!size-3"
          style={{ color, borderColor: `${color}55`, backgroundColor: `${color}14` }}
        >
          {/* The pill's color is the stage, so it skips the dot to sit on one
              line with the decision pill in the 256px panel. */}
          <SelectValue>{label}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {MANUAL_STAGES.map((status) => {
            const option = DOCUMENT_STAGE_CONFIG[statusStage(status, analyzed)];
            return (
              <SelectItem key={status} value={status}>
                <span className="inline-flex items-center gap-1.5">
                  <StageDot color={option.color} />
                  {option.label}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * A finalized document whose redacted file no longer reflects the case's rules: a
 * rule that matches its text changed after it was finalized (FR-14). Hover lists why.
 */
export function OutOfDateBadge({ doc }: { doc: CaseDocument }) {
  if (!doc.out_of_date) return null;
  const reasons = doc.stale_reasons ?? [];
  return (
    <IconTooltip label={reasons.length ? reasons.join('\n') : 'A rule changed after it was finalized'}>
      <span
        data-testid="out-of-date"
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none bg-amber-100 text-amber-800"
      >
        Out of date
      </span>
    </IconTooltip>
  );
}
